<template>
  <div class="lifestyle mb-12">
    <div v-if="loading">
      <v-progress-circular
        :size="20"
        color="primary"
        class="mr-2"
        indeterminate
      ></v-progress-circular>
      Loading...
    </div>

    <v-tabs v-else v-model="currentTab">
      <v-tab>Lifestyle</v-tab>
      <v-tab-item>
        <v-data-table
          v-model="selectedLifestyle"
          :headers="lifestyleHeaders"
          :items="lifestyleList"
          :items-per-page="-1"
          item-key="LifestyleId"
          show-select
          single-select
          dense
          class="lifestyleTable"
          @click:row="editLifestyle"
        >
          <template v-slot:[`item.Percentage`]="{ item }">
            {{ Number(item.Percentage).toFixed(4) }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-icon :disabled="!showFormButtons" @click.stop="deleteLifestyle(item)"
              >mdi-delete</v-icon
            >
          </template>
        </v-data-table>
        <v-dialog
          v-model="showDialog"
          width="500"
          @keydown.esc="cancelChange"
          @keydown.enter="saveChanges"
          persistent
        >
          <v-card>
            <v-card-text class="pt-5 pb-0">
              <v-container>
                <v-form ref="form" v-model="isFormValid" lazy-validation>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="editedLifestyle.Name"
                        label="Name"
                        :rules="rules"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="6">
                      <v-select
                        v-model="editedLifestyle.TargetDate"
                        :items="availableTargetDate"
                        label="Target date"
                        :rules="rules"
                      ></v-select>
                    </v-col>

                    <v-col cols="6">
                      <v-text-field
                        v-model="editedLifestyle.Percentage"
                        label="Percentage"
                        type="number"
                        min="0"
                        max="100"
                        step="0.0001"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="6">
                      <v-checkbox
                        v-model="editedLifestyle.Interpolation"
                        label="Interpolation"
                      ></v-checkbox>
                    </v-col>
                     <v-col cols="6">
                      <v-checkbox
                        v-model="editedLifestyle.LeftToRightOnly"
                        label="Left to Right Only"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </v-form>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" outlined @click="cancelChange"> Cancel </v-btn>
              <v-btn color="primary" depressed @click="saveChanges"> Ok </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <Dialog ref="confirmationDialog" v-model="dialog" />
      </v-tab-item>

      <v-tab>Matrix</v-tab>
      <v-tab-item>
        <LifestyleMatrix
          :lifestyle="selectedLifestyle"
          :addColCount="addColCount"
          :addRowCount="addRowCount"
          :saveMatrixCount="saveMatrixCount"
          :showFormButtons="showFormButtons"
          @validateRows="(val) => (areAllRowsValid = val)"
          @validateYears="(val) => (areAllYearsDifferent = val)"
          @validateSave="(val) => (isSaveDisabled = val)"
          @validateAddRow="(val) => (isAddRowDisabled = val)"
        />
      </v-tab-item>
    </v-tabs>

    <v-footer
      v-if="showFormButtons"
      padless
      fixed
      outlined
      class="pl-5 pr-5 pt-3 pb-3"
      color="white"
      elevation="3"
    >
      <div v-if="currentTab === 0" class="btnsContainer">
        <v-spacer></v-spacer>
        <v-btn depressed color="primary" @click="editLifestyle(null)"> New Lifestyle </v-btn>
      </div>

      <div v-if="currentTab === 1" class="btnsContainer">
        <div class="footerWarnings">
          <span v-if="!areAllRowsValid" class="red--text mr-4">
            * Sum of all funds in every row should be equal to 100%
          </span>
          <span v-if="!areAllYearsDifferent" class="red--text">
            * There should be no duplicated YTR in table
          </span>
        </div>
        <!-- <v-spacer></v-spacer> -->
        <div class="actionBtns">
          <v-btn outlined color="primary" class="ml-4" @click="++addColCount"> Add column </v-btn>
          <v-btn
            outlined
            color="primary"
            class="ml-4"
            @click="++addRowCount"
            :disabled="isAddRowDisabled"
          >
            Add row
          </v-btn>
          <v-btn
            depressed
            color="primary"
            class="ml-4"
            @click="++saveMatrixCount"
            :disabled="isSaveDisabled"
          >
            Save matrix
          </v-btn>
        </div>
      </div>
    </v-footer>
  </div>
</template>

<script>
import LifestyleMatrix from './LifestyleMatrix.vue';
import Dialog from '@/components/layout/Dialog.vue';

export default {
  name: 'Lifestyle',
  components: { LifestyleMatrix, Dialog },

  data() {
    return {
      loading: false,
      currentTab: 0,

      lifestyleList: [],
      selectedLifestyle: [],
      lifestyleHeaders: [
        { text: 'Lifestyle name', value: 'Name' },
        { text: 'Target date', value: 'TargetDate' },
        { text: 'Percentage', value: 'Percentage', align: 'end' },
        { text: '', value: 'actions', sortable: false },
      ],
      availableTargetDate: ['NRD', 'SPA', 'Target Date'],
      editedLifestyle: {},
      editedIndex: -1,
      defaultLifestyle: {
        LifestyleId: 0,
        PlanId: this.currentPlanId,
        Name: null,
        TargetDate: null,
        TargetDateValue: 0,
        Percentage: 0,
        LeftToRightOnly: '',
        Interpolation: false,
      },

      showDialog: false,
      isFormValid: false,
      rules: [(v) => !!v || 'Requered.'],

      dialog: false,

      // matrix counts
      addColCount: 0,
      addRowCount: 0,
      saveMatrixCount: 0,
      areAllRowsValid: true,
      areAllYearsDifferent: true,
      isAddRowDisabled: false,
      isSaveDisabled: false,
    };
  },

  computed: {
    currentPlanId() {
      return this.$store.getters.currentPlanId;
    },
    showFormButtons() {
      const userPermission = this.$store.getters.getUserPermission(this.$route.path);
      if (userPermission && userPermission == 'Full') return true;

      return false;
    },
  },

  watch: {
    currentPlanId() {
      this.getLifestyleList();
    },
  },

  created() {
    this.getLifestyleList();
    this.$root.currentPage = 'Investment';
  },

  destroyed() {
    this.$root.currentPage = null;
  },

  methods: {
    editLifestyle(item) {
      if (!this.showFormButtons) return;
      this.showDialog = true;
      if (item) {
        this.editedIndex = this.lifestyleList.indexOf(item);
        this.editedLifestyle = { ...item };
      } else {
        this.editedLifestyle = { ...this.defaultLifestyle };
        this.editedIndex = -1;
      }
    },

    cancelChange() {
      this.showDialog = false;
      this.editedIndex = -1;
      this.editedLifestyle = { ...this.defaultLifestyle };
    },

    saveChanges() {
      this.isFormValid = this.$refs.form.validate();
      if (this.isFormValid) {
        if (this.editedIndex === -1) this.editedLifestyle.PlanId = this.currentPlanId;

        this.updateLifestyle();
        this.showDialog = false;
      }
    },

    async getLifestyleList() {
      this.loading = true;
      try {
        const response = await this.$api.get(
          `/lifestyle/GetLifestyleList?planId=${this.currentPlanId}`
        );

        this.lifestyleList = response.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async updateLifestyle() {
      this.loading = true;
      try {
        await this.$api.post(`/lifestyle/UpdateLifestyle`, this.editedLifestyle);

        this.getLifestyleList();
        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Lifestyle is updated successfully',
        });
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async deleteLifestyle(item) {
      if (!this.showFormButtons) return;
      await this.$refs.confirmationDialog.open(
        'Confirmation',
        'Are you sure you wish to delete this lifestyle?'
      );

      this.loading = true;
      try {
        await this.$api.delete(`/lifestyle/DeleteLifestyle?lifestyleId=${item.LifestyleId}`);

        this.getLifestyleList();
        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Lifestyle is deleted',
        });
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.lifestyle {
  margin-bottom: 20px;
  padding-left: 30px;
  padding-right: 30px;
  .v-tabs {
    margin-bottom: 10px;

    .v-tabs-slider-wrapper {
      width: 0px !important;
      height: 0px !important;
    }

    .v-tab {
      border: 1px solid;
      border-radius: 30px;
      color: #4a1cd4 !important;
      height: 35px;
      margin-right: 10px;

      &.v-tab--active {
        color: white !important;
        background-color: #4a1cd4;
      }
    }
  }

  .lifestyleTable {
    max-width: 960px;
  }

  .btnsContainer {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .footerWarnings {
      flex-shrink: 1;
      display: flex;
      flex-wrap: wrap;
    }

    .actionBtns {
      flex-shrink: 0;
    }
  }
}
</style>
